import {
  Image,
  Switch,
  Button,
  Upload,
  UploadProps,
  message,
  Flex,
} from "antd";
import "./consultation_details.css";
import { useEffect, useState } from "react";
import ConsultationRecordApi from "../../apis/consultationRecordApi";
import { useParams } from "react-router-dom";
import wordIcon from "../../images/icon-word.png";
import pdfIcon from "../../images/icon-pdf.png";
import TextArea from "antd/es/input/TextArea";
import { PlusOutlined } from "@ant-design/icons";

interface UserInfo {
  name: string;
  gender?: "man" | "women";
  age: number;
  height: string;
  weight: string;
  contactWay: string;
  location: string;
  symptomDescription: string;
  supplementaryMaterials: string;
  typeList: TypeListItem[];
  patientVisible: boolean;
  fourDiagnosis: string;
  dialectical: string;
  analysis: string;
  prescription: string;
}

interface TypeListItem {
  typeId: string;
  typeName: string;
  questions: QuestionsItem[];
}

export interface QuestionsItem {
  questionId: string;
  type: number;
  question: string;
  describe: string;
  required: boolean;
  limitOptionsNumber: number;
  index: number;
  answer: string;
  serious: boolean;
  options: OptionsItem[];
}

interface OptionsItem {
  optionId: string;
  questionOption: string;
  index: number;
  noneStatus: boolean;
  otherStatus: boolean;
}

interface OSSDataType {
  keyId: string;
  dir: string;
  expire: string;
  host: string;
  policy: string;
  signature: string;
}

const ConsultationDetails: React.FC = () => {
  const { userId } = useParams();
  const [messageApi, contextHolder] = message.useMessage();
  const [userInfo, setUserInfo] = useState<UserInfo>();
  // 文件访问前缀
  const [OssUrl, setOssUrl] = useState<string>("");
  // 图片上传
  const [OSSData, setOSSData] = useState<OSSDataType>();

  useEffect(() => {
    getOssUrl();
    getRecordDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // 获取图片上传地址
  const getOssUrl = () => {
    ConsultationRecordApi.getOssUrl().then((value) => {
      const newOssData: OSSDataType = value.data;
      newOssData.dir += "/";
      setOSSData(newOssData);
    });

    ConsultationRecordApi.getOssHost().then((value) => {
      setOssUrl(value.data);
    });
  };

  const getRecordDetails = () => {
    userId !== undefined &&
      ConsultationRecordApi.getRecordDetails(userId).then((value) => {
        setUserInfo(value.data);
      });
  };

  const ReturnAttachment = (url: string) => {
    let src = url;
    if (
      url.indexOf(".doc") !== -1 ||
      url.indexOf(".docx") !== -1 ||
      url.indexOf(".docm") !== -1 ||
      url.indexOf(".dotx") !== -1 ||
      url.indexOf(".dotm") !== -1 ||
      url.indexOf(".xml") !== -1
    ) {
      src = wordIcon;
    } else if (url.indexOf(".pdf") !== -1) {
      src = pdfIcon;
    }

    return <Image preview={false} width={60} height={60} src={src} />;
  };

  const getExtraData: UploadProps["data"] = (file) => ({
    key: file.url,
    OSSAccessKeyId: OSSData?.keyId,
    policy: OSSData?.policy,
    Signature: OSSData?.signature,
  });

  const beforeUpload: UploadProps["beforeUpload"] = async (file) => {
    if (!OSSData) {
      messageApi.error("上传失败，请重新尝试!");
      return false;
    }

    const expire = Number(OSSData.expire) * 1000;

    if (expire < Date.now()) {
      await getOssUrl();
    }

    const suffix = file.name.slice(file.name.lastIndexOf("."));
    const filename = Date.now() + suffix;
    // @ts-ignore
    file.url = OSSData.dir + filename;

    return file;
  };

  const handleChange: UploadProps["onChange"] = ({ fileList }) => {
    if (fileList.length && fileList[0].status === "done") {
      const img = OssUrl + fileList[0].url;
      if (userInfo) {
        userInfo.prescription && userInfo.prescription.length > 0
          ? (userInfo.prescription += "," + img)
          : (userInfo.prescription = img);
        setUserInfo({ ...userInfo });
      }
    }
  };

  const uploadProps: UploadProps = {
    name: "file",
    action: OSSData?.host,
    maxCount: 1,
    data: getExtraData,
    onChange: handleChange,
    beforeUpload,
    showUploadList: false,
    listType: "picture-card",
  };

  return (
    <div className="ConsultationDetails textStyle">
      {contextHolder}
      <div style={{ display: "flex" }}>
        <div className="pageItem" style={{ flex: 1 }}>
          <h1>
            <span>问诊详情</span>
            <Button type="link" onClick={() => window.history.back()}>
              返回上一页
            </Button>
          </h1>
          <h3>基本信息</h3>

          <div className="pageItem_block_column pageItem1_1">
            <div className="pageItem_block">
              <span className="textAshColor titleOneLEtter">就诊人：</span>
              <span>
                {userInfo
                  ? `${userInfo.name}，${
                      userInfo.gender === "man" ? "男" : "女"
                    }，${userInfo.age}岁，${userInfo.height}cm，${
                      userInfo.weight
                    }kg`
                  : ""}
              </span>
            </div>

            <div className="pageItem_block" style={{ display: "flex" }}>
              <span className="textAshColor titleOneLEtter">联系方式：</span>
              <span>{userInfo?.contactWay}</span>
            </div>

            <div className="pageItem_block" style={{ display: "flex" }}>
              <span className="textAshColor titleOneLEtter">所属地：</span>
              <span>{userInfo?.location}</span>
            </div>

            <div className="pageItem_block">
              <span className="textAshColor titleOneLEtter">补充：</span>
              <div className="flexBlockCenter">
                {userInfo?.supplementaryMaterials &&
                  userInfo.supplementaryMaterials
                    .split(",")
                    .map((src, index) => (
                      <a
                        style={{ marginRight: 10 }}
                        key={index}
                        href={src}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {ReturnAttachment(src)}
                        <div style={{ textAlign: "center" }}>
                          附件{index + 1}
                        </div>
                      </a>
                    ))}
              </div>
            </div>

            <div className="pageItem_block">
              <span className="textAshColor titleOneLEtter">主诉：</span>
              <span style={{ color: 'red', fontWeight: 700 }}>{userInfo?.symptomDescription}</span>
            </div>
          </div>
        </div>

        {userInfo && userInfo.typeList.length > 0 && (
          <div className="pageItem" style={{ flex: 3 }}>
            <div className="flexBlockCenter">
              <h3>症状详情</h3>
              <span
                className="textAshColor"
                style={{ marginLeft: "10px", color: "red" }}
              >
                注意：红色文字是患者更显著的症状
              </span>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", marginTop: 20 }}>
              {userInfo.typeList &&
                userInfo.typeList.map((item, index) => {
                  return (
                    <div key={index} style={{ width: "33%", marginTop: 20 }}>
                      <h4>{item.typeName}</h4>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {item.questions &&
                          item.questions.map((questionsItem, key) => {
                            switch (questionsItem.type) {
                              case 3:
                                return (
                                  <div
                                    key={key}
                                    style={{ minWidth: "100%", marginTop: 10 }}
                                  >
                                    <div className="textAshColor titleOneLEtter">
                                      {questionsItem.question}：
                                    </div>
                                    {questionsItem.answer &&
                                      questionsItem.answer.split(",").map(
                                        (src, num) =>
                                          src !== "" && (
                                            <div
                                              key={num}
                                              style={{
                                                display: "inline-block",
                                                marginRight: 10,
                                                marginTop: 10
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <Image
                                                  key={num}
                                                  src={src}
                                                  width={100}
                                                  height={100}
                                                />
                                                <span style={{ marginTop: 5 }}>
                                                  {
                                                    questionsItem.options[num]
                                                      .questionOption
                                                  }
                                                </span>
                                              </div>
                                            </div>
                                          )
                                      )}
                                  </div>
                                );
                              default:
                                return (
                                  <div
                                    key={key}
                                    style={{ minWidth: "100%", marginTop: 10 }}
                                  >
                                    <span
                                      className="textAshColor titleOneLEtter"
                                      style={{
                                        color: questionsItem.serious
                                          ? "red"
                                          : undefined,
                                      }}
                                    >
                                      {questionsItem.question}：
                                    </span>
                                    <span>
                                      {questionsItem.type === 2
                                        ? questionsItem.answer ?? "无"
                                        : questionsItem.answer}
                                    </span>
                                  </div>
                                );
                            }
                          })}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {userInfo && (
        <div className="blockItem">
          <div className="flexBlockCenter">
            <h3>医生诊断</h3>
            <span className="textAshColor" style={{ marginLeft: "10px" }}>
              患者可见
            </span>
            <Switch
              value={userInfo.patientVisible}
              style={{ marginLeft: "10px" }}
              onChange={(v) => {
                userInfo.patientVisible = v;
                setUserInfo({ ...userInfo });
              }}
            />
          </div>

          <div
            className="pageItem_block"
            style={{ width: 400, paddingLeft: 10 }}
          >
            <span className="textAshColor titleOneLEtter">四诊：</span>
            <TextArea
              showCount
              value={userInfo?.fourDiagnosis}
              onChange={(e) => {
                if (userInfo) {
                  userInfo.fourDiagnosis = e.target.value;
                  setUserInfo({ ...userInfo });
                }
              }}
              placeholder="请输入四诊"
              style={{ height: 90, resize: "none" }}
            />
          </div>

          <div className="pageItem_block medalBefore" style={{ width: 400 }}>
            <span className="请输入辩证">辩证：</span>
            <TextArea
              showCount
              value={userInfo.dialectical}
              onChange={(e) => {
                userInfo.dialectical = e.target.value;
                setUserInfo({ ...userInfo });
              }}
              placeholder="请输入辩证"
              style={{ height: 90, resize: "none" }}
            />
          </div>

          <div
            className="pageItem_block"
            style={{ width: 400, paddingLeft: 10 }}
          >
            <span className="textAshColor titleOneLEtter">分析：</span>
            <TextArea
              showCount
              value={userInfo.analysis}
              onChange={(e) => {
                userInfo.analysis = e.target.value;
                setUserInfo({ ...userInfo });
              }}
              placeholder="请输入分析"
              style={{ height: 90, resize: "none" }}
            />
          </div>

          <div className="pageItem_block" style={{ paddingLeft: 10 }}>
            <span className="textAshColor titleOneLEtter">处 方：</span>
            {userInfo &&
              userInfo.prescription &&
              userInfo.prescription.split(",").map((src, index) => (
                <div
                  key={index}
                  style={{ marginRight: 15, width: 100, height: 100 }}
                >
                  <Image width={100} height={100} src={src} />
                </div>
              ))}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Upload {...uploadProps}>
                <PlusOutlined
                  style={{ color: "#D9D9D9", fontSize: 24, fontWeight: 900 }}
                />
              </Upload>
              <span style={{ marginTop: 5, fontSize: 12, color: "#999" }}>
                请上传处方
              </span>
            </div>
          </div>

          <Flex vertical={false} style={{ paddingLeft: 60, paddingTop: 20 }}>
            <Button type="default" onClick={() => window.history.back()}>
              取消
            </Button>

            <Button
              style={{ marginLeft: 20 }}
              type="primary"
              onClick={() => {
                if (!userInfo.dialectical) {
                  messageApi.error("请输入辩证");
                  return;
                }

                messageApi.loading({
                  duration: 0,
                  content: "",
                  key: "doctorSetDiagnosisLoading",
                });

                ConsultationRecordApi.doctorSetDiagnosis(
                  userId!,
                  userInfo.patientVisible,
                  userInfo.dialectical,
                  userInfo.fourDiagnosis,
                  userInfo.analysis,
                  userInfo.prescription
                ).then(() => {
                  messageApi.destroy("doctorSetDiagnosisLoading");
                  messageApi.success(`提交成功`);
                });
              }}
            >
              确定
            </Button>
          </Flex>
        </div>
      )}
    </div>
  );
};

export default ConsultationDetails;
